<template>
  <v-form ref="printer" v-model="modelValue.validPrinter">
    <div class="printer-selection">
      <v-layout class="pl-2">
        <v-flex>
          <p class="title">{{ title }}</p>
          <p class="grey--text caption">{{ titleCaption }}</p>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex class="d-flex justify-end" shrink>
          <v-btn
            :loading="loader"
            @click="testPrint()"
            :disabled="
              modelValue.printer_type == '0' || !modelValue.validPrinter
            "
            :icon="isMobile"
            :flat="isMobile"
            color="primary"
          >
            <v-icon :left="!isMobile">flaticon-printer</v-icon>
            <span v-show="!isMobile"> TEST PRINT</span>
          </v-btn>
        </v-flex>
      </v-layout>

      <v-layout wrap align-center class="px-2">
        <v-flex sm6 xs12>
          <div class="subheading">Printer Type</div>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex sm6 xs12>
          <v-select
            v-model="modelValue.printer_type"
            :items="printerTypes"
            item-text="name"
            item-value="value"
            @change="onChangePrinterType()"
            required
            :rules="requiredRule"
            outline
            hide-details
            single-line
          >
            <template slot="item" slot-scope="data">
              <v-list-tile-content>
                <v-list-tile-title>{{data.item.name}}</v-list-tile-title>
                <v-list-tile-sub-title class="caption">{{
                  data.item.comment
                }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-select>
        </v-flex>
      </v-layout>
      <v-layout
        wrap
        align-center
        v-if="modelValue.printer_type != '0'"
        class="px-2 mt-4"
      >
        <v-flex sm6 xs12>
          <div class="subheading">Choose Printer</div>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex sm6 xs12>
          <v-select
            v-model="modelValue.printer_id"
            :items="printers"
            item-text="name"
            item-value="id"
            required
            :rules="printerRule"
            outline
            @change="onChangePrinter"
            hide-details
            single-line
            :loading="loadingPrinters"
          >
            <template slot="item" slot-scope="data">
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ data.item.name }}
                  <span class="caption" v-if="data.item.caption"
                    >({{ data.item.caption }})</span
                  >
                </v-list-tile-title>
                <v-list-tile-sub-title class="caption">{{
                  data.item.comment
                }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
            <template slot="selection" slot-scope="data">
              {{ data.item.name }}
              <span class="caption " v-if="data.item.caption"
                >({{ data.item.caption }})</span>
            </template>
          </v-select>
        </v-flex>
      </v-layout>
      <v-layout
        wrap
        align-center
        class="px-2 mt-4"
        v-if="modelValue.printer_type == 'ip' && modelValue.printer_id == 0"
      >
        <v-flex sm6 xs12>
          <div class="subheading">Printer IP</div>
        </v-flex>
        <v-spacer></v-spacer>
        <v-flex sm6 xs12>
          <v-text-field
            v-model="modelValue.printer_ip"
            required
            :rules="ruleIP"
            outline
            single-line
            @input="$emit('input', modelValue)"
          ></v-text-field>
        </v-flex>
      </v-layout>
    </div>
  </v-form>
</template>

<script>
import { requestInfo } from "@/providers/RoloProvider";
import PrintUtility from "@/modules/printManager/PrintUtility";
import { PRINTER_TYPES } from "@/modules/printManager/Utils";
import printerMixin from "@/modules/printManager/PrinterMixin";

export default {
  name: "PrinterForm",
  props: {
    value: Object,
    title: {
      type: String,
      required: true,
    },
    titleCaption: {
      type: String,
      required: true,
    },
  },
  mixins: [printerMixin],
  data() {
    return {
      printerTypes: PrintUtility.getSupportedPrinterTypes(),
      loadingPrinters: false, // loader for fetching printers
      loader: false, // loader for test print
      printers: [],
      requiredRule: [(v) => !!v || "This field is required."],
      printerRule: [
        (v) => (v !== null && v !== undefined) || "This field is required.",
      ],
      ruleIP: [
        (v) => !!v || "IP address is required",
        (v) => {
          const ipPattern = /^(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)$/;
          return ipPattern.test(v) || "IP address must be valid";
        },
      ],
    };
  },
  watch: {
    "modelValue.printer_type": {
      handler(newType, oldType) {
        if (newType !== oldType || oldType === undefined) {
          this.onChangePrinterType(true);
        }
      },
      immediate: true,
    },
  },
  computed: {
    modelValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", Object.assign({}, val));
      },
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  methods: {
    // get network printers from server
    async getNetworkPrinters() {
      const req = requestInfo();
      const storeId = localStorage.getItem("storeId");
      const url = `${this.$apiURL}store/${storeId}/node/${
        req.info.node_id
      }/get_available_printers`;
      return this.$http
        .get(url)
        .then((resp) => {
          let printers = [
            { id: 0, name: "— MANUAL IP  —", ip: "", caption: "" },
          ];
          resp.body.data.forEach((p) => {
            printers.push({
              id: p.id,
              name: p.name,
              ip: p.ipAddress,
              comment: p.comment,
              caption: p.ipAddress,
            });
          });
          return printers;
        })
        .catch((failure) => {
          console.error(failure);
          return [];
        });
    },

    // on change printer type reset selected printer and fetched printer/option based on printer type
    async onChangePrinterType(no_reset = false) {
      this.loadingPrinters = true;
      this.printers = [];
      if (!no_reset) {
        this.$emit(
          "input",
          Object.assign({}, this.value, {
            printer_id: null,
            printer_name: null,
            printer_ip: null,
          })
        );
      }
      switch (this.modelValue.printer_type) {
        case "ip":
          this.printers = await this.getNetworkPrinters();
          break;
        case "bluetooth":
          this.printers = await PrintUtility.getBluetoothDevices();
          break;
        case "usb":
          this.printers = await PrintUtility.getUsbDevices();
          break;
      }
      this.loadingPrinters = false;
    },

    // onChangePrinter : ..
    onChangePrinter(id) {
      const printer = this.printers.find((p) => p.id === id);
      let selected = {
        printer_id: null,
        printer_name: "",
        printer_ip: null,
        device_id: null,
      };
      if (printer) {
        selected.printer_id = printer.id;
        selected.printer_name = printer.name;
        selected.printer_ip = printer.ip;
        selected.device_id = printer.id;
      }

      this.$emit("input", Object.assign({}, this.value, selected));
    },

    // testPrint allows to check the selected printer is accessible
    testPrint() {
      let loadingMessage;
      if (this.modelValue.printer_type === PRINTER_TYPES.NETWORK.value) {
        loadingMessage = `Connecting printer ${this.modelValue.printer_ip}`;
      } else {
        loadingMessage = `Connecting printer ${this.modelValue.printer_id}`;
      }

      this.loader = true;
      this.$root.$loader.start(loadingMessage, {});
      const nodeId = requestInfo().info.node_id;
      const storeId = localStorage.getItem("storeId");
      const url = `${this.$apiURL}store/${storeId}/node/${nodeId}/test_print`;
      const req = {
        printer: {
          printer_type: this.modelValue.printer_type,
          printer_id: this.modelValue.id,
          printer_name: this.modelValue.name,
          printer_ip: this.modelValue.printer_ip,
        },
      };

      this.$http
        .put(url, req)
        .then(function(resp) {
          this.loader = false;
          this.$root.$loader.stop();
          this.callPrinter(resp.body.print_data, resp.body.message);
        })
        .catch((failure) => {
          // console.log("Failure Object:", failure);
          this.loader = false;
          this.$root.$loader.stop();
          let msg = "Unexpected error"; // Default error message
          if (failure.body) {
            msg = failure.body.message || msg;
          } else if (failure.message) {
            msg = failure.message;
          }

          this.$store.commit("showSnackbar", {
            message: msg,
            color: "error",
          });
        });
    },
  },
};
</script>
